// frontend/src/services/apiService.js
class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || '/api';
  }

  getToken() {
    return localStorage.getItem('adminToken');
  }

  async makeRequest(endpoint, options = {}) {
    const token = this.getToken();
    
    // Préparer les headers de base
    const headers = {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    // IMPORTANT: N'ajouter Content-Type que si ce n'est PAS du FormData
    // Le navigateur gérera automatiquement le Content-Type pour FormData
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const config = {
      ...options,
      headers,
    };

    console.log('🌐 API Request:', {
      method: options.method || 'GET',
      endpoint,
      hasToken: !!token,
      fullUrl: `${this.baseURL}${endpoint}`,
      isFormData: options.body instanceof FormData
    });

    const response = await fetch(`${this.baseURL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ API Error:', {
        status: response.status,
        endpoint,
        error: errorData
      });

      // Session expirée/invalide : déconnexion automatique, sauf sur la page
      // de connexion elle-même (un mauvais mot de passe renvoie aussi 401)
      const isAuthEndpoint = endpoint.startsWith('/auth/login') || endpoint.startsWith('/auth/register');
      if (response.status === 401 && token && !isAuthEndpoint) {
        localStorage.removeItem('adminToken');
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login?expired=1';
        }
      }

      throw new Error(errorData.error || `Erreur ${response.status}`);
    }

    return response.json();
  }

  // Méthodes génériques
  async get(endpoint, config = {}) {
    return this.makeRequest(endpoint, {
      method: 'GET',
      ...config,
    });
  }

  async post(endpoint, data, config = {}) {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
      ...config,
    });
  }

  async put(endpoint, data, config = {}) {
    return this.makeRequest(endpoint, {
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data),
      ...config,
    });
  }

  async delete(endpoint, config = {}) {
    return this.makeRequest(endpoint, {
      method: 'DELETE',
      ...config,
    });
  }
  
  // Dépenses
  async getExpenses(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.makeRequest(`/expenses${queryString ? `?${queryString}` : ''}`);
  }

  async createExpense(expenseData) {
    return this.makeRequest('/expenses', {
      method: 'POST',
      body: expenseData, // FormData pour les fichiers
    });
  }

  async updateExpense(id, expenseData) {
    return this.makeRequest(`/expenses/${id}`, {
      method: 'PUT',
      body: expenseData,
    });
  }

  async deleteExpense(id) {
    return this.makeRequest(`/expenses/${id}`, {
      method: 'DELETE',
    });
  }
  
  // Auth
  async login(email, password) {
    console.log('Email avant envoi:', email);
    return this.makeRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getCurrentUser() {
    return this.makeRequest('/auth/me');
  }

  // Members
  async getMembers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.makeRequest(`/members${queryString ? `?${queryString}` : ''}`);
  }

  async getMember(id) {
    return this.makeRequest(`/members/${id}`);
  }

  async createMember(memberData) {
    const token = this.getToken();
    const formData = new FormData();
    
    Object.keys(memberData).forEach(key => {
      if (memberData[key] !== null && memberData[key] !== undefined) {
        formData.append(key, memberData[key]);
      }
    });

    const response = await fetch(`${this.baseURL}/members`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Erreur ${response.status}`);
    }

    return response.json();
  }

  async updateMember(id, memberData) {
    const token = this.getToken();
    const formData = new FormData();
    
    console.log('=== apiService.updateMember ===');
    console.log('memberData reçu:', memberData);
    
    Object.keys(memberData).forEach(key => {
      if (memberData[key] !== null && memberData[key] !== undefined) {
        console.log(`Ajout au FormData: ${key} =`, memberData[key]);
        if (Array.isArray(memberData[key])) {
          memberData[key].forEach(val => formData.append(key, val));
        } else {
          formData.append(key, memberData[key]);
        }
      }
    });
    
    console.log('=== FormData créé, envoi vers backend ===');

    const response = await fetch(`${this.baseURL}/members/${id}`, {
      method: 'PUT',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || errorData.error || `Erreur ${response.status}`);
      error.errors = errorData.errors;
      error.data = errorData;
      throw error;
    }

    return response.json();
  }

  async deleteMember(id) {
    return this.makeRequest(`/members/${id}`, {
      method: 'DELETE',
    });
  }

  // Enrollment
  async submitEnrollment(enrollmentData) {
    return this.makeRequest('/enrollment', {
      method: 'POST',
      body: JSON.stringify(enrollmentData),
    });
  }

  async getPendingEnrollments() {
    return this.makeRequest('/enrollment/pending');
  }

  async approveEnrollment(id) {
    return this.makeRequest(`/enrollment/${id}/approve`, {
      method: 'POST',
    });
  }

  async rejectEnrollment(id) {
    return this.makeRequest(`/enrollment/${id}/reject`, {
      method: 'POST',
    });
  }

  async getMemberMetadata() {
    return this.makeRequest('/members/meta/groups-and-functions');
  }

  // Contributions
  async getContributions(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.makeRequest(`/contributions${queryString ? `?${queryString}` : ''}`);
  }

  async getContributionTypes() {
    return this.makeRequest('/contributions/types/list');
  }

  async createContributionType(typeData) {
    return this.makeRequest('/contributions/types', {
      method: 'POST',
      body: JSON.stringify(typeData),
    });
  }

  // Worship Sessions
  async getWorshipSessions() {
    return this.makeRequest('/worship-sessions');
  }

  async createWorshipSession(sessionData) {
    return this.makeRequest('/worship-sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  }

  async updateWorshipSession(id, sessionData) {
    return this.makeRequest(`/worship-sessions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(sessionData),
    });
  }

  // Blog
  async getBlogPosts() {
    return this.makeRequest('/blog/posts');
  }

  async createBlogPost(postData) {
    return this.makeRequest('/blog/posts', {
      method: 'POST',
      body: JSON.stringify(postData),
    });
  }

  async updateBlogPost(id, postData) {
    return this.makeRequest(`/blog/posts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(postData),
    });
  }

  async deleteBlogPost(id) {
    return this.makeRequest(`/blog/posts/${id}`, {
      method: 'DELETE',
    });
  }

  // Admin Management
  async setAdminRights(email, isAdmin) {
    return this.makeRequest('/admin/set-rights', {
      method: 'POST',
      body: JSON.stringify({ email, is_admin: isAdmin }),
    });
  }

  // Statistics
  async getStatistics() {
    return this.makeRequest('/statistics');
  }
}

export const apiService = new ApiService();